import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, PLATFORM_ID, ViewChild } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-background',
  templateUrl: './background.component.html',
  styleUrl: './background.component.scss'
})
export class BackgroundComponent implements AfterViewInit, OnDestroy {
  @ViewChild('canvas', { static: true })
  canvasRef!: ElementRef<HTMLCanvasElement>;

  private ctx!: CanvasRenderingContext2D;
  private time = 0;
  private animFrameId!: number;
  private isMobile = false;

  // ── Desktop config (full quality — 120×120 = 14,400 dots) ──────────────────
  private desktopConfig = {
    rows: 120,
    cols: 120,
    waveSpeed: 0.015,
    perspective: 500,
    viewDistance: 100,
    cameraHeight: 900,
    dotBaseSize: 3.5,
    spacingX: 60,
    spacingZ: 40,
    waveAmpZ: 90,
    waveAmpX: 40,
    twistStrength: 50,
    fogDensity: 0.35
  };

  // ── Mobile config (60×60 = 3,600 dots — 75% less CPU, same visual) ─────────
  private mobileConfig = {
    rows: 60,
    cols: 60,
    waveSpeed: 0.015,
    perspective: 500,
    viewDistance: 100,
    cameraHeight: 900,
    dotBaseSize: 3.5,
    spacingX: 60,
    spacingZ: 40,
    waveAmpZ: 90,
    waveAmpX: 40,
    twistStrength: 50,
    fogDensity: 0.35
  };

  // Automatically picks the right config based on screen size
  private get config() {
    return this.isMobile ? this.mobileConfig : this.desktopConfig;
  }

  // 60fps on desktop, 30fps on mobile
  private targetFps = 60;
  private lastFrameTime = 0;

  constructor(@Inject(PLATFORM_ID) private platformId: object) { }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return; // SSR guard

    const canvas = this.canvasRef.nativeElement;
    const context = canvas.getContext('2d');
    if (!context) return;

    this.ctx = context;
    this.resizeCanvas();
    this.animate(0);
  }

  ngOnDestroy(): void {
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId); // stops the loop when page navigates away
    }
  }

  @HostListener('window:resize')
  resizeCanvas(): void {
    const canvas = this.canvasRef.nativeElement;
    this.isMobile = window.innerWidth <= 768;        // detect mobile on every resize
    this.targetFps = this.isMobile ? 30 : 60;        // switch fps target
    canvas.width = window.innerWidth;
    canvas.height = window.innerWidth > 768 ? 898 : 1342;
  }

  // ── Throttle wrapper — skips frames to hit the target fps ──────────────────
  private animate(timestamp: number): void {
    const minInterval = 1000 / this.targetFps;
    const elapsed = timestamp - this.lastFrameTime;

    if (elapsed >= minInterval) {
      this.lastFrameTime = timestamp - (elapsed % minInterval);
      this.drawFrame();
    }

    this.animFrameId = requestAnimationFrame((t) => this.animate(t));
  }

  // ── All the actual drawing logic (unchanged from your original) ─────────────
  private drawFrame(): void {
    const canvas = this.canvasRef.nativeElement;
    const ctx = this.ctx;
    const width = canvas.width;
    const height = canvas.height;

    // Background
    ctx.fillStyle = '#020617';
    ctx.fillRect(0, 0, width, height);

    this.time += this.config.waveSpeed;

    const vanishingPointX = width / 2;
    const vanishingPointY = height * 0.3;
    ctx.fillStyle = 'rgb(0, 51, 255)';

    for (let z = this.config.rows - 1; z >= 0; z--) {
      for (let x = 0; x < this.config.cols; x++) {
        const lateralTwist =
          Math.sin(z * 0.06 + x * 0.02 + this.time) *
          this.config.twistStrength;

        const x3d =
          (x - this.config.cols / 2) * this.config.spacingX +
          lateralTwist;

        const z3d = z * this.config.spacingZ;

        const twistedWaveZ =
          Math.sin(z * 0.06 + x * 0.03 - this.time) *
          this.config.waveAmpZ;

        const bigWaveX =
          Math.sin(x * 0.04 + this.time * 0.5) *
          this.config.waveAmpX;

        const detail =
          Math.sin(x * 0.15 + z * 0.1 + this.time) * 15;

        const distFromCenter = Math.abs(x - this.config.cols / 2);
        const curvature = Math.pow(distFromCenter, 2) * 0.25;

        const y3d = twistedWaveZ + bigWaveX + detail - curvature;

        const scale =
          this.config.perspective /
          (this.config.perspective + this.config.viewDistance + z3d);

        const x2d = x3d * scale + vanishingPointX;
        const y2d =
          (y3d + this.config.cameraHeight) * scale +
          vanishingPointY;

        const size = this.config.dotBaseSize * scale * 2.5;
        const opacity = Math.max(
          0,
          Math.min(1, scale * 3.5 - this.config.fogDensity)
        );

        if (opacity > 0.01) {
          ctx.globalAlpha = opacity;
          ctx.beginPath();
          ctx.arc(x2d, y2d, size, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    // Vignette overlay
    ctx.globalAlpha = 1;
    const gradient = ctx.createRadialGradient(
      width / 2, 0, 0,
      width / 2, 0,
      Math.max(width, height) * 1.2
    );
    gradient.addColorStop(0, 'rgba(2, 6, 23, 0)');
    gradient.addColorStop(0.3, 'rgba(2, 6, 23, 0)');
    gradient.addColorStop(1, 'rgba(2, 6, 23, 0.95)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  }
}